import { BuildWorkflow } from "projen/lib/build";
import { JobPermission } from "projen/lib/github/workflows-model";

/** Job ID of the main build job (Projen constant). */
const BUILD_JOB_ID = "build";

/** Name of the gate job appended to build workflows (ADR 0004). */
export const COMPLETE_JOB_ID = "complete";

/**
 * Projen's BuildWorkflow holds the underlying GithubWorkflow in a private
 * property. We use this shape to access it (cast via unknown).
 */
interface BuildWorkflowWithWorkflow {
  workflow: {
    jobs: Record<string, unknown>;
    addJob(id: string, job: Record<string, unknown>): void;
  };
}

/**
 * Builds the run script for the complete job step. Requires the build job to
 * succeed; all other jobs must succeed or be skipped (ADR 0004).
 * Uses `\$` so the output contains literal `${{ }}` for GitHub Actions expressions.
 */
function buildCompleteJobRunScript(jobIds: string[]): string {
  const lines: string[] = [];
  for (const id of jobIds) {
    const expr = "${{ needs." + id + ".result }}";
    if (id === BUILD_JOB_ID) {
      lines.push(
        `if [ "${expr}" != "success" ]; then echo "Job ${id} must succeed"; exit 1; fi`,
      );
    } else {
      lines.push(
        `if [ "${expr}" != "success" ] && [ "${expr}" != "skipped" ]; then echo "Job ${id} must succeed or be skipped"; exit 1; fi`,
      );
    }
  }
  return lines.join("\n");
}

/**
 * Appends the "complete" gate job to a build workflow (ADR 0004, issue #165).
 * The job runs after all other jobs, uses {@code if: always()}, and succeeds
 * only when the build job succeeded and every other job succeeded or was skipped.
 * Call this for the default build workflow and for any build workflow created
 * by Configulator (e.g. AwsDeployWorkflow).
 *
 * @param buildWorkflow - The Projen BuildWorkflow to append the complete job to.
 */
export function addBuildCompleteJob(buildWorkflow: BuildWorkflow): void {
  const w = (buildWorkflow as unknown as BuildWorkflowWithWorkflow).workflow;
  if (!w?.jobs || typeof w.addJob !== "function") {
    return;
  }
  const jobIds = Object.keys(w.jobs).filter((id) => id !== COMPLETE_JOB_ID);
  if (jobIds.length === 0) {
    return;
  }
  const runScript = buildCompleteJobRunScript(jobIds);
  w.addJob(COMPLETE_JOB_ID, {
    name: "Complete",
    needs: jobIds,
    if: "always()",
    runsOn: ["ubuntu-latest"],
    permissions: {
      contents: JobPermission.NONE,
    },
    steps: [
      {
        name: "Verify all jobs succeeded or were skipped",
        run: runScript,
      },
    ],
  });
}
